import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router';
import { useForm } from 'react-hook-form';

import Modal from 'components/ui/Modal';
import { ConfirmButton } from 'components/ui/Button';
import { CHANGE_TRAVEL_PASS_DATES } from 'store/mutations';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { Column } from 'components/shared/Layout';

import {
  StyledTextInput,
  StyledRow,
  FormDate,
  StyledForm,
  StyledConfirm,
  StyledNoRevert,
  StyledDateField,
  StyledCalendar
} from '../SubscriptionDetails.styles';

import { StyledLabel, ThalysPassDetailsContainer, StyledCalendarIcon } from '../../../BuyThalysPass/BuyThalysPass.styles';

import TextInput from 'components/ui/TextInput';
import { SubscriptionStatusCode } from 'constants/subscriptions';
import CHANGE__PENDING_TRAVEL_PASS_DATES from 'store/mutations/CHANGE__PENDING_TRAVEL_PASS_DATES';

export const ChangeStartDate = ({
  onClose,
  travelStartDate,
  travelDateEnd,
  travelPassReference,
  code,
  status,
  bookingReference,
  membershipNumber
}) => {
  const history = useHistory();
  const location = useLocation();

  const defaultValues = {
    startDate: moment(travelStartDate).format('DD-MM-YYYY')
  };

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  const { register, handleSubmit, getValues, setValue } = formMethods;
  const formStartDate = getValues().startDate;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [endDate, setEndDate] = useState(moment(travelDateEnd).format('DD-MM-YYYY'));
  const maxExtendMonthsWindow = /[6]/.test(code) ? 6 : 12;

  const hasPendingPayment = status === SubscriptionStatusCode.PENDING_PAYMENT;

  const [changeTravelPassDates, { loading: changeTravelPassLoading, error: changeTravelPassError }] = useMutation(
    hasPendingPayment ? CHANGE__PENDING_TRAVEL_PASS_DATES : CHANGE_TRAVEL_PASS_DATES,
    {
      onCompleted: () => {
        onClose();
        history.replace({
          ...location,
          state: {
            shouldRetry: true
          }
        });
      },
      refetchQueries: ['profile']
    }
  );

  const tomorrow = moment().add(1, 'days');
  const originalStartDate = moment(travelStartDate);
  const aMonthFromOriginalStartDate = moment(travelStartDate).add(1, 'months');
  const cannotChangeDates = moment(moment(formStartDate, 'DD-MM-YYYY').format('YYYY-MM-DD')).isSame(
    moment(travelStartDate).format('YYYY-MM-DD')
  );

  const handleCalendarChange = (event) => {
    setValue('startDate', moment(event).format('DD-MM-YYYY'), {
      shouldValidate: false
    });
    setEndDate(moment(event).add(maxExtendMonthsWindow, 'months').format('DD-MM-YYYY'));
    setCalendarOpen(false);
  };

  const handleOnSubmit = async (data) => {
    await changeTravelPassDates({
      variables: {
        travelPassReference,
        newStartDate: `${moment(data.startDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}T00:00:00.000Z`,
        newEndDate: `${moment(endDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}T00:00:00.000Z`,
        bookingReference
      }
    });
  };

  if (changeTravelPassError) {
    throw new AgentPortalError(changeTravelPassError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/clubeurostar/${membershipNumber}#subscriptions`
    });
  }

  return (
    <Modal isOpen={true} title={<FormattedMessage id="subscriptions.changeStartDate" />} onClose={onClose}>
      <StyledForm data-testid="change-start-date-form" onSubmit={handleSubmit(handleOnSubmit)}>
        <ThalysPassDetailsContainer>
          <StyledLabel data-testid="startDate-open-label" htmlFor="startDate-label">
            <FormattedMessage id="subscriptions.startDate" />:
          </StyledLabel>
          <FormDate>
            <StyledDateField data-testid="startDate-open" htmlFor="startDate" onClick={() => setCalendarOpen(!calendarOpen)}>
              <TextInput
                data-cy="startDate"
                data-testid="startDate"
                type="text"
                readOnly
                id="startDate"
                value={formStartDate}
                placeholder="DD-MM-YYYY"
                {...register('startDate', { required: true })}
                width={'100%'}
              />
              <StyledCalendarIcon size={20} />
            </StyledDateField>
          </FormDate>
          {calendarOpen && (
            <StyledCalendar
              id="styled-calendar"
              showNeighboringMonth={false}
              tabIndex="-1"
              value={originalStartDate.toDate()}
              onChange={handleCalendarChange}
              minDate={tomorrow.toDate()}
              maxDate={aMonthFromOriginalStartDate.toDate()}
            />
          )}
        </ThalysPassDetailsContainer>
        {hasPendingPayment ? (
          <StyledRow columns={12}>
            <Column span={6}>
              <StyledLabel htmlFor="subscriptions.currentStartDate">
                <FormattedMessage id="subscriptions.currentStartDate" />:
              </StyledLabel>
              <StyledTextInput
                name="currentStartDate"
                type="text"
                value={moment(travelStartDate).format('DD-MM-YYYY')}
                readOnly
                data-cy="currentStartDate"
                data-testid="currentStartDate"
              />
            </Column>

            <Column span={6}>
              <StyledLabel htmlFor="subscriptions.newStartDate">
                <FormattedMessage id="subscriptions.newStartDate" />:
              </StyledLabel>
              <StyledTextInput
                name="startDate"
                id="startDate"
                type="text"
                value={formStartDate}
                readOnly
                data-cy="newStartDate"
                data-testid="newStartDate"
              />
            </Column>
          </StyledRow>
        ) : (
          <StyledRow columns={12}>
            <Column span={6}>
              <StyledLabel htmlFor="subscriptions.currentEndDate">
                <FormattedMessage id="subscriptions.currentEndDate" />:
              </StyledLabel>
              <StyledTextInput
                name="currentEndDate"
                type="text"
                value={moment(travelDateEnd).format('DD-MM-YYYY')}
                readOnly
                data-cy="currentEndDate"
                data-testid="currentEndDate"
              />
            </Column>

            <Column span={6}>
              <StyledLabel htmlFor="subscriptions.newEndDate">
                <FormattedMessage id="subscriptions.newEndDate" />:
              </StyledLabel>
              <StyledTextInput
                name="endDate"
                id="endDate"
                type="text"
                value={endDate}
                readOnly
                data-cy="newEndDate"
                data-testid="newEndDate"
              />
            </Column>
          </StyledRow>
        )}

        <StyledConfirm>
          <ConfirmButton
            type="submit"
            data-testid="submit"
            data-cy="submit"
            loading={changeTravelPassLoading}
            disabled={changeTravelPassLoading || cannotChangeDates}
            variant="primary"
          >
            <FormattedMessage id="extras.hotel.modal.confirm" />
          </ConfirmButton>
        </StyledConfirm>
      </StyledForm>

      {cannotChangeDates && (
        <StyledNoRevert>
          <FormattedMessage
            id="subscriptions.cannotChangeDatesError"
            values={{ travelStartDate: moment(travelStartDate).format('DD-MM-YYYY') }}
          />
        </StyledNoRevert>
      )}
    </Modal>
  );
};

ChangeStartDate.propTypes = {
  onClose: PropTypes.func,
  travelStartDate: PropTypes.string,
  travelDateEnd: PropTypes.string,
  travelPassReference: PropTypes.string,
  code: PropTypes.string,
  status: PropTypes.string,
  bookingReference: PropTypes.string,
  membershipNumber: PropTypes.string
};
